import { Component, HostBinding, Input } from '@angular/core';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { IconComponent } from '../icon/icon.component';
import { RouterLink } from '@angular/router';
import { NgIf } from '@angular/common';
import { SharedModule } from '../../shared/shared.module';

export type ButtonType =
  | 'primary'
  | 'primary-btn'
  | 'secondary'
  | 'secondary-btn'
  | 'tertiary'
  | 'ai'
  | 'view-as-btn'
  | 'danger'
  | 'danger-flat'
  | 'success'
  | 'danger-btn'
  | 'warning'
  | 'transparent'
  | 'primary-text'
  | 'primary-text-outlined'
  | 'primary-outlined'
  | 'outlined-gay'
  | 'outlined'
  | 'transparent';

@Component({
  selector: 'ui-button',
  standalone: true,
  imports: [NgIf, SvgIconComponent, IconComponent],
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
})
export class ButtonComponent {
  @Input() label!: string | undefined;
  @Input() abbreviation!: string | undefined;
  /** Legacy asset-based icon name (src/assets/icones). Prefer `lucideIcon`. */
  @Input() icon?: string;
  /**
   * Lucide icon name. Takes precedence over `icon` when both are set, so a button
   * can be migrated to Lucide without touching its other bindings.
   */
  @Input() lucideIcon?: string;
  @Input() iconPosition: 'start' | 'end' = 'start';
  @Input() iconSize: 'small' | 'medium' | 'large' = 'medium';
  @Input() rotated: boolean = false;
  @Input() type: ButtonType = 'primary';
  @Input() disabled = false;
  @Input() fullWidth = false;
  @Input() isLoading = false;
  @Input() align: 'start' | 'center' | 'end' = 'center';
  @Input() counter?: number;

  /** Lucide's own scale, mapped from this button's icon size. */
  get lucideSize(): 'small' | 'medium' | 'large' {
    return this.iconSize;
  }
  /**
   * Accessible name, required when the button is icon-only. It has to land on
   * the inner <button> — putting `aria-label` on the <ui-button> host leaves the
   * focusable element nameless, and a tooltip only contributes a description.
   */
  @Input() ariaLabel?: string;
  /** Forwarded onto the inner <button> as `data-testid`; unset by default. */
  @Input() testId?: string;

  // Conditionally apply the class on the host element
  @HostBinding('class.full-width') get isFullWidth() {
    return this.fullWidth;
  }
}
